<template>
  <div class="workspace" :key="updateCardKey">
    <div v-if="!selectedWorkspace" class="no workspace-card">
      <span>{{ $t("SelectWorkspace") }}</span>
    </div>
    <div v-if="!!selectedWorkspace" class="workspace-info-container">
      <object-main-info
        :fields="fields"
        :identifier="cardObjectIdentifier"
        :identifier-label="$t('Name')"
        :editing-data="editingData"
        :show-edit-button="!creatingWorkspace"
        :card-label="cardLabel"
        :height="'40%'"
        :expand-card="editingData"
        :submit-button-label="submitButtonLabel"
        :required-fields-filled="requiredFieldsAreFilled"
        :isWorkspaceObject="editingData"
        showEditActionButtons
        @toggle-edit="toggleEdit"
        @save-object="saveObject"
        @update-object="updateWorkspace"
        @cancel-edit="discardChangesDialogIsOpen = true"
      >
        <template v-slot:buttons-before-edit-slot>
          <custom-button
            v-if="!creatingWorkspace"
            :icon="'mdi-open-in-new'"
            :icon-size="13"
            :height="'20px'"
            :width="'20px'"
            :button-styling="buttonStyling"
            @buttonClick="openWorkspace"
          />
        </template>
      </object-main-info>
      <workspace-specific-info
        :users="selectedWorkspace.users ? selectedWorkspace.users : []"
        :companies="
          selectedWorkspace.companies ? selectedWorkspace.companies : []
        "
        :textGeom="selectedWorkspace.wp_geo_json"
        :editing="editingData"
        @update-map-data="updateWorkspaceMap($event)"
      />
    </div>
    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="#29BDF0"
      style="position: absolute"
    />
    <dialog-choice
      :isOpen="saveChangesDialogIsOpen"
      :title="$t('SaveChanges')"
      :content="$t('SaveChangesContent')"
      @mainAction="saveObject"
      :mainActionLabel="$t('Confirm')"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="saveChangesDialogIsOpen = false"
      icon="mdi-content-save"
      iconColor="#29BDF0"
    />
    <dialog-choice
      :isOpen="discardChangesDialogIsOpen"
      :title="$t('DiscardChanges')"
      :content="$t('ClusterCreationConfirmation')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="cancelEdit"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="discardChangesDialogIsOpen = false"
      icon="mdi-delete"
      iconColor="#29BDF0"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { bus } from "@/main.js";
import _ from "lodash";
import utils from "@/utils";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { objectManagementMixin } from "@/mixins/objectManagementMixin";
import LoadingSpinner from "vue-loading-overlay";
import ObjectMainInfo from "@/newComponents/_Organisms/ObjectMainInfo";
import DialogChoice from "@/newComponents/_Molecules/DialogChoice.vue";
import { activitiesTasksDataMixin } from "@/mixins/field/activitiesTasksDataMixin";
import { listsMixin } from "@/mixins/listsMixin";
import WorkspaceSpecificInfo from "@/newComponents/Directory/WorkspaceSpecificInfo";
import CustomButton from "@/newComponents/_Atoms/CustomButton.vue";
export default {
  name: "Workspace",

  components: {
    LoadingSpinner,
    DialogChoice,
    ObjectMainInfo,
    WorkspaceSpecificInfo,
    CustomButton,
  },

  mixins: [
    checkPermissionsMixin,
    objectManagementMixin,
    activitiesTasksDataMixin,
    listsMixin,
  ],

  props: {
    selectedWorkspace: {
      type: Object,
    },
  },

  data() {
    return {
      dialog: true,
      loading: false,
      workspace: null,
      editingData: false,
      originalWorkspace: {},
      networkRulesOptions: [],
      specificRequiredFieldsAreFilled: false,
      dataToSend: {},
      managerOptions: [],
      actionToExecute: "UPDATE_SELECTED_WORKSPACE",
      submitButtonLabel: "Save",
      cardLabel: "EditWorkspace",
      saveChangesDialogIsOpen: false,
      discardChangesDialogIsOpen: false,
      networkLevelOptions: [],
      companiesOptions: [],
      labellingRulesOptions: [],
      projectPhases: [],
      buttonStyling: {
        padding: "0px",
        "drop-shadow": "0.85px 0.85px 1.75px rgba(0, 0, 0, 0.25)",
        "border-radius": "4px",
      },

      updateCardKey: 0,
    };
  },

  mounted() {
    this.getProjectPhases();
    this.loadManagerOptions();
    this.getNetworkLevels();
    this.loadNetworkRules();
    this.loadLabellingRules();
    this.getcompaniesOptions();
  },

  watch: {
    selectedWorkspace(value) {
      if (value !== null && Object.keys(value).length === 0) {
        this.setCreateWorkspaceVariables();
      } else if (!!value) {
        this.setEditWorkspaceVariables();
      } else if (value === null) {
        this.clearVariables();
      }
    },
    editingData() {
      if(this.editingData) {
        bus.$emit("set-drawable");
      } else {
        bus.$emit("set-undrawable");
      }
      bus.$emit("toggle-create-edit-flag")
    },
    async "workspace.companies"(companies) {
    // if no companies selected -> clear manager/users
    if (!companies || companies.length === 0) {
      this.managerOptions = [];
      if (this.workspace) {
        this.workspace.users = [];
        this.workspace.manager = null;
      }
      return;
    }
    
    try {
      const responses = await Promise.all(
        companies.map((companyId) => this.fetchUsersByCompanyID(companyId))
      );
      
      const userMap = new Map();

      for (const resp of responses) {
        // handle different response shapes: resp.data.data or resp.data
        const users = (resp && resp.data && (resp.data.data || resp.data)) || [];
        for (const user of users) {
          const userId = user.id || user.user_id || user.uuid;
          const uid = String(userId || "");
          const type = (user.type || "").toString().toLowerCase();
          if (!uid) continue;

          if (!userMap.has(uid)) {
            const fullName =
              user.full_name ||
              [user.first_name, user.last_name].filter(Boolean).join(" ") ||
              user.email ||
              uid;
            userMap.set(uid, { code: userId, libelle: fullName });
          }
        }
      }

      this.managerOptions = Array.from(userMap.values());

      // Preserve selected users that still exist in managerOptions
      if (this.workspace && Array.isArray(this.workspace.users)) {
        const available = new Set(this.managerOptions.map((u) => String(u.code)));
        this.workspace.users = this.workspace.users.filter((id) =>
          available.has(String(id))
        );

        // Clear manager if no longer available
        if (this.workspace.manager && !available.has(String(this.workspace.manager))) {
          this.workspace.manager = null;
        }
      }
    } catch (err) {
      console.error(err);
    }
    },
  },

  computed: {
    requiredFieldsAreFilled() {
      return this.workspace
        ? !!this.workspace.label &&
            !!this.workspace.status &&
            !!this.workspace.engineering_rule_id &&
            !!this.workspace.labelling_rule_id &&
            !!this.workspace.text_geom
        : false;
    },

    creatingWorkspace() {
      return this.actionToExecute === "CREATE_WORKSPACE";
    },

    height() {
      return this.editingData ? "50%" : "60%";
    },

    fields() {
      let fields = [];
      if (this.workspace !== null) {
        if (this.editingData) {
          fields.splice(0, 0, {
            label: this.$i18n.tc("Label", 1),
            value: this.workspace.label,
            varName: "label",
            inputType: "text",
            disabled: false,
            required: true,
            size: "50%",
          });
          fields.splice(1, 0, {
            label: this.$i18n.t("ProjectPhase"),
            value: this.workspace.status,
            varName: "status",
            inputType: "select",
            disabled: false,
            required: true,
            options: this.projectPhases,
            size: "50%",
          });
          fields.splice(2, 0, {
            label: this.$i18n.t("PlanName"),
            value: this.workspace.plan_name,
            varName: "plan_name",
            inputType: "text",
            disabled: false,
            required: false,
            size: "50%",
          });
          fields.splice(3, 0, {
            label: this.$i18n.t("NetworkRules"),
            value: this.workspace.engineering_rule_id,
            varName: "engineering_rule_id",
            inputType: "select",
            disabled: false,
            options: this.networkRulesOptions,
            required: true,
            size: "50%",
          });
          fields.splice(4, 0, {
            label: this.$i18n.t("LabellingRules"),
            value: this.workspace.labelling_rule_id,
            varName: "labelling_rule_id",
            inputType: "select",
            disabled: false,
            options: this.labellingRulesOptions,
            required: true,
            size: "50%",
          });
          fields.splice(5, 0, {
            label: this.$i18n.t("Manager"),
            value: this.workspace.manager,
            varName: "manager",
            inputType: "select",
            disabled: false,
            options: this.managerOptions,
            required: false,
            size: "50%",
          });
          fields.splice(6, 0, {
            label: this.$i18n.tc("Company", 2),
            value: this.workspace.companies,
            varName: "companies",
            inputType: "multiselect",
            disabled: false,
            options: this.companiesOptions,
            required: false,
            size: "50%",
            height: 0,
            inputStyle: {
              height: '5px'
            },
          });
          fields.splice(7, 0, {
            label: this.$i18n.tc("User", 2),
            value: this.workspace.users,
            varName: "users",
            inputType: "multiselect",
            disabled: false,
            options: this.managerOptions,
            required: false,
            size: "50%",
            height: 50,
            inputStyle: {
              height: "20px",
            },
          });
          fields.splice(8, 0, {
            label: this.$i18n.t("Status"),
            value: this.workspace.status_active ? "True" : "False",
            varName: "status_active",
            inputType: "radio",
            disabled: false,
            radioOptions: [
              { value: "True", label: this.$tc("OpenNoun", 1) },
              { value: "False", label: this.$tc("ClosedNoun", 1) },
            ],
            required: false,
            size: "33%",
          });
          fields.splice(9, 0, {
            label: this.$i18n.t("Access"),
            value: this.workspace.access_active ? "True" : "False",
            varName: "access_active",
            inputType: "radio",
            disabled: false,
            radioOptions: [
              { value: "True", label: this.$i18n.t("Visible") },
              { value: "False", label: this.$i18n.t("NotVisible") },
            ],
            required: false,
            size: "34%",
          });
          fields.splice(10, 0, {
            label: this.$i18n.t("AllowNotifications"),
            value: this.workspace.notify ? "True" : "False",
            varName: "notify",
            inputType: "radio",
            disabled: false,
            radioOptions: [
              { value: "True", label: this.$i18n.t("Yes") },
              { value: "False", label: this.$i18n.t("No") },
            ],
            required: false,
            size: "33%",
          });
        }

        if (!this.editingData) {
          fields.splice(0, 0, {
            icon: "mdi-transit-connection-variant",
            label: this.$i18n.t("NetworkRules"),
            value: this.getNetworkRuleName(this.workspace.engineering_rule_id),
            varName: "engineering_rule_id",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(1, 0, {
            icon: "mdi-tag",
            label: this.$i18n.t("LabellingRules"),
            value: this.getLabellingRuleName(this.workspace.labelling_rule_id),
            varName: "labelling_rule_id",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(2, 0, {
            icon: "mdi-clipboard-text",
            label: this.$i18n.t("ProjectPhase"),
            value: this.getProjectPhaseName(this.workspace.status),
            varName: "status",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(3, 0, {
            icon: "mdi-clipboard-check",
            label: this.$i18n.t("Status"),
            value: this.workspace.status_active ? "Open" : "Closed",
            varName: "status_active",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(4, 0, {
            icon: "mdi-eye",
            label: this.$i18n.t("Access"),
            value: this.workspace.access_active ? "Visible" : "Not visible",
            varName: "status_active",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(5, 0, {
            icon: "mdi-calendar",
            label: this.$i18n.t("CreationDate"),
            value: this.workspace.created_at
              ? this.dateFormatter(this.workspace.created_at)
              : "",
            varName: "created_at",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(6, 0, {
            icon: "mdi-account-tie",
            label: this.$i18n.t("Manager"),
            value: this.getUserName(this.workspace.manager),
            varName: "manager",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(7, 0, {
            icon: "mdi-file-tree",
            label: this.$i18n.t("NetworkLevel"),
            value: this.getNetworkLevelName(this.workspace.network_level_id),
            varName: "network_level_id",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(8, 0, {
            icon: "mdi-label",
            label: this.$i18n.t("PlanName"),
            value: this.workspace.plan_name,
            varName: "plan_name",
            inputType: "text",
            disabled: false,
            required: false,
            size: "100%",
          });
        }
      }
      return fields;
    },

    cardObjectIdentifier() {
      if (this.workspace) {
        return this.workspace.label;
      }
      return "";
    },
  },

  methods: {
    ...mapActions([
      "fetchWorkspaceByUUID",
      "fetchAllUsers",
      "fetchUserCompanies",
      "fetchUsersByCompanyID",
      "FETCH_ALL_NETWORK_RULES_TEMPLATES",
      "fetchAllCompanies",
    ]),

    openWorkspace() {
      this.$router.push({
        name: "home",
        params: { workspace_uuid: this.workspace.wp_uuid },
      });
    },

    async getWorkspaceByID() {
      let uuid = this.selectedWorkspace.wp_uuid;
      this.loading = true;
      try {
        this.workspace = await this.fetchWorkspaceByUUID({
          uuid: uuid,
          includes: "users,companies",
          fields:
            "wp_uuid,wp_status,wp_id,wp_engineering_rule_id,wp_label,wp_plan_name,wp_network_level_id,wp_labelling_rule_id,wp_manager,wp_status_active,wp_access_active,wp_text_geom,wp_created_at,wp_notify",
        });
        this.prepareWorkspaceData();
      } catch (error) {
        //await this.$router.replace("/error");
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    prepareWorkspaceData() {
      let usersfetched = this.workspace.users;
      let manager = this.workspace.wp_manager;
      this.updateFetchedCompanies().then(this.updateFetchedUsers(usersfetched, manager));

      this.workspace.plan_name = this.workspace.wp_plan_name;
      this.workspace.access_active = this.workspace.wp_access_active;
      this.workspace.status_active = this.workspace.wp_status_active;
      this.workspace.labelling_rule_id = this.workspace.wp_labelling_rule_id;
      this.workspace.engineering_rule_id =
        this.workspace.wp_engineering_rule_id;
      this.workspace.manager = this.workspace.wp_manager;
      this.workspace.status = this.workspace.wp_status;
      this.workspace.label = this.workspace.wp_label;
      this.workspace.text_geom = this.workspace.wp_text_geom;
      this.workspace.created_at = this.workspace.wp_created_at;
      this.workspace.notify = this.workspace.wp_notify;
      this.originalWorkspace = _.cloneDeep(this.workspace);
    },

    updateFetchedCompanies(){
      return new Promise((resolve, reject) => {
        if (this.workspace.companies) {
          let companiesAux = [];
          this.workspace.companies.forEach((company) => {
            companiesAux.push(company.id);
          });
          this.workspace.companies = companiesAux;
        }
        resolve();
      });
    },

    updateFetchedUsers(usersfetched, manager){
      if (usersfetched) {
        setTimeout(() => {
          let usersAux = [];
          usersfetched.forEach((user) => {
            usersAux.push(user.id);
          });
          this.workspace.users = usersAux;
          this.workspace.manager = manager;
        },500);
      }
    },

    updateWorkspaceMap(mapData) {
      this.workspace.text_geom = mapData;
    },

    async getcompaniesOptions() {
      this.loading = true;
      try {
        let companies = await this.fetchAllCompanies({ paginate: false });

        companies.data.forEach((company) => {
          this.companiesOptions.push({ code: company.id, label: company.name });
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    getProjectPhases() {
      this.GET_LIST({ listName: "list_statut", code: [], includeDeleted: false })
        .then((projectPhases) => {
          projectPhases.forEach((projectPhase) => {
            this.projectPhases.push({
              code: projectPhase.code,
              libelle: projectPhase.libelle,
            });
          });
        })
        .catch((error) => {
          console.error(error);
        });
    },

    getProjectPhaseName(id) {
      if (id && this.projectPhases.length > 0) {
        let phase = this.projectPhases.find((item) => item.code === id);
        return this.getListElementLabel(phase);
      } else return id;
    },

    getNetworkLevels() {
      this.networkLevelOptions = this.$store.getters.getListOfOptionsByListName(
        "list_infra_type_log",
        [],
        true
      );
    },

    getNetworkRuleName(id) {
      if (id && this.networkRulesOptions.length > 0) {
        let networkRule = this.networkRulesOptions.find(
          (item) => item.code === id
        );
        return this.getListElementLabel(networkRule);
      } else return id;
    },

    getNetworkLevelName(id) {
      if (id && this.networkLevelOptions.length > 0) {
        let networkLevel = this.networkLevelOptions.find(
          (item) => item.code === id
        );
        return this.getListElementLabel(networkLevel);
      } else return id;
    },

    updateWorkspace(updatedValue) {
      this.workspace[updatedValue.varName] = updatedValue.value;
    },

    cancelEdit() {
      this.discardChangesDialogIsOpen = false;
      this.editingData = false;
      if (this.workspace.wp_uuid) {
        this.workspace = _.cloneDeep(this.originalWorkspace);
        bus.$emit("draw-workspace", this.selectedWorkspace.wp_geo_json);
      } else {
        this.$emit("clear-selected-item");
      }
    },

    toggleEdit() {
      if (this.editingData) {
        this.discardChangesDialogIsOpen = true;
      } else {
        this.editingData = !this.editingData;
      }
    },

    async saveObject() {
      this.saveChangesDialogIsOpen = false;
      try {
        this.createDataToSend();
        if (Object.keys(this.dataToSend).length > 0) {
          let data;
          if (!this.creatingWorkspace) {
            this.dataToSend.projection = 3857;
            data = {
              id: this.workspace.wp_id,
              data: this.dataToSend,
            };
          } else {
            this.dataToSend.projection = 3857;
            data = this.dataToSend;
          }
          await this.$store.dispatch(this.actionToExecute, data);
        }

        this.editingData = false;

        if (!this.creatingWorkspace) {
          this.getWorkspaceByID();
          this.$toasted.global.toast_success(
            this.$i18n.t("WorkspaceEditedSuccessfully")
          );
        } else {
          this.$toasted.global.toast_success(
            this.$i18n.t("WorkspaceCreatedSuccessfully")
          );
        }

        bus.$emit("reload-workspaces");
      } catch (error) {
        console.error(error);
        this.handleError(error);
      } finally {
        this.loading = false;
        this.dataToSend = {};
      }
    },

    prepareListOfIDsToSend(listOfIDs) {
      let preparedList = [];
      for (let itemID of listOfIDs) {
        preparedList.push({ id: itemID });
      }
      return preparedList;
    },

    prepareListOfUUIDsToSend(objId, listOfElements) {
      let element = listOfElements.find((obj) => obj.code === objId);
      return { uuid: element.uuid };
    },

    loadManagerOptions() {
      if (this.userHasManagementPermission("user_list")) {
        if (this.isSuperAdmin || this.isAdmin) {
          this.getAllUsers();
        } else if (this.userHasManagementPermission("user_company_list")) {
          this.getUserCompaniesUsers();
        } else {
          this.managerOptions = [];
        }
      } else if (this.selectedWorkspace.manager) {
        this.managerOptions = [this.workspace.manager];
      } else {
        this.managerOptions = [];
      }
    },

    async loadLabellingRules() {
      try {
        let templates = await this.FETCH_ALL_NETWORK_RULES_TEMPLATES({
          type: "type",
          value: "Labelling",
        });
        templates.data.forEach((template) => {
          this.labellingRulesOptions.push({
            code: template.id,
            libelle: template.name,
            uuid: template.uuid,
          });
        });
      } catch (error) {
        console.error(error);
      }
    },

    getLabellingRuleName(id) {
      if (id && this.labellingRulesOptions.length > 0) {
        let labelling_rule = this.labellingRulesOptions.find(
          (item) => item.code === id
        );
        return this.getListElementLabel(labelling_rule);
      } else return id;
    },

    async loadNetworkRules() {
      try {
        let templates = await this.FETCH_ALL_NETWORK_RULES_TEMPLATES({
          type: "type",
          value: "Calculation",
          queryParams: {
            paginate: false
          }
        });
        templates.data.forEach((template) => {
          this.networkRulesOptions.push({
            code: template.id,
            libelle: template.name,
            uuid: template.uuid,
          });
        });
      } catch (error) {
        console.error(error);
      }
    },

    getUserCompaniesUsers() {
      let currentUserID = this.$store.getters.currentUserID;
      this.fetchUserCompanies({ id: currentUserID })
        .then((companies) => {
          let allUsers = [];
          companies.forEach((company) => {
            this.fetchUsersByCompanyID(company.id).then((resp) => {
              let users = resp.data.data;
              for (let user of users) {
                if (
                  !allUsers.some((item) => item.id === user.id) &&
                  user.type === "web"
                ) {
                  allUsers.push(this.sortUserOptionByWeb(user));
                }
              }
            });
          });
          this.managerOptions = allUsers;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    getAllUsers() {
      let allUsers = [];
      this.fetchAllUsers({})
        .then((resp) => {
          let users = resp.data.data;
          for (let user of users) {
            if (user.type === "web") {
              allUsers.push(this.sortUserOptionByWeb(user));
            }
          }
          this.managerOptions = allUsers;
        })
        .catch((error) => {
          console.error(error);
        });
    },

    getUserName(id) {
      if (id && this.managerOptions.length > 0) {
        let user = this.managerOptions.find((user) => user.code == id);
        return user ? (user.libelle ? user.libelle : id) : id;
      } else {
        return id;
      }
    },

    sortUserOptionByWeb(user) {
      let label = user.first_name;
      if (user.last_name) {
        label += " " + user.last_name;
      }
      return {
        id: user.id,
        label: label,
        type: user.type,
      };
    },

    createDataToSend() {
      Object.keys(this.workspace).forEach((key) => {
        if (this.workspace[key] !== this.originalWorkspace[key]) {
          this.dataToSend[key] = this.workspace[key];
        }
      });
      if (this.dataToSend.users.length > 0) {
        let users = this.prepareListOfIDsToSend(this.dataToSend.users);
        this.dataToSend.users = users;
      } else {
        delete this.dataToSend.users;
      }
      if (this.dataToSend.companies.length > 0) {
        let companies = this.prepareListOfIDsToSend(this.dataToSend.companies);
        this.dataToSend.companies = companies;
      } else {
        delete this.dataToSend.companies;
      }

      if(this.dataToSend.engineering_rule_id || this.creatingWorkspace){
        this.dataToSend.engineering_rule = this.prepareListOfUUIDsToSend(
          this.dataToSend.engineering_rule_id,
          this.networkRulesOptions
        );
      }
      if(this.dataToSend.labelling_rule_id || this.creatingWorkspace){
        this.dataToSend.labelling_rule = this.prepareListOfUUIDsToSend(
          this.dataToSend.labelling_rule_id,
          this.labellingRulesOptions
        );
      }
      delete this.dataToSend.engineering_rule_id;
      delete this.dataToSend.labelling_rule_id;
    },

    handleError(error) {
      let errorHandle = utils.errorHandler(error);

      if (typeof errorHandle === "object") {
        if (errorHandle.hasOwnProperty("label")) {
          if (errorHandle.label[0].includes('min')) {
          this.$toasted.global.toast_error(this.$i18n.t('WorkspaceLabelMin'));
          }
        }
      } else if (typeof errorHandle === "string") {
        this.$toasted.global.toast_error(errorHandle);
      }
    },

    setCreateWorkspaceVariables() {
      this.workspace = {
        label: undefined,
        labelling_rule_id: null,
        engineering_rule_id: null,
        access_active: true,
        status_active: true,
        plan_name: null,
        manager: null,
        companies: [],
        users: [],
        status: null,
        text_geom: null,
        request_method: "GET",
      };
      this.originalWorkspace = {};
      this.actionToExecute = "CREATE_WORKSPACE";
      this.submitButtonLabel = "Create";
      this.cardLabel = "CreateWorkspace";
      this.editingData = true;
    },

    setEditWorkspaceVariables() {
      this.getWorkspaceByID();
      this.actionToExecute = "EDIT_WORKSPACE";
      this.submitButtonLabel = "Save";
      this.cardLabel = "workspace_update";
    },

    clearVariables() {
      this.workspace = null;
      this.originalWorkspace = null;
      this.editingData = false;
    },

    requiredFieldsValidation() {
      return this.requiredFieldsAreFilled;
    },

    dateFormatter(date) {
      const dateArray = date.split(" ");
      return dateArray[0] + " / " + dateArray[1];
    },
  },
};
</script>

<style scoped>
.workspace {
  height: 100%;
  max-height: 100%;
  background-color: #fff;
  position: relative;
  border-radius: 8px;
}

.workspace-card {
  height: 100%;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}

.no.workspace-card {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
}

.no.workspace-card > span {
  color: #29bdf0;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
}

.workspace-info-container {
  gap: 8px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/Directory/Workspace.vue